import {
    Breakpoints,
    Text,
    TextTag,
    TokenTextAppearance,
    TokenTextColor,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'
import React, {useEffect, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {RootState} from '../rootReducer'

import {useTranslation} from 'react-i18next'
import {Model} from '../../types/Model'
import {SingleSimulation} from '../../types/SingleSimulation'
import isEmpty from 'lodash.isempty'
import {
    setAdvance,
    setFuels,
    setModels,
    setMonthlyRange,
    setPeriod,
    setTotKm,
} from '../../features/filterValues/filterValuesSlice'
import {FilterSelect} from '../components/FilterSelect'
import {FilterSlider} from '../components/FilterSlider'
import {isBetweenRange, numberFormat} from '../../shared/utilities'
import {FILTERS_KEYS, Global, QUERY_PARAMS} from '../../shared/variables'
import {AppDispatch} from '../thunk'

const FilterContainer = styled.div`
    padding-top: ${(props) => props.theme.size.static650};
`

const HeaderContainer = styled.div`
    display: none;

    @media (min-width: ${Breakpoints.b1280}px) {
        display: grid;
        grid-template-columns: 5fr 2fr;
        grid-gap: ${(props) => props.theme.size.static400};
        padding-bottom: ${(props) => props.theme.size.static500};
    }
`

const FilterGroupLegend = styled.legend`
    border: 0;
    clip: rect(0 0 0 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
`
const TextContainer = styled.div`
    display: none;

    @media (min-width: ${Breakpoints.b1280}px) {
        display: inline-block;
        border-bottom: ${({theme}) => theme.size.border.active} solid
            ${({theme}) => theme.colors.border.divider};
    }
`

const HeaderText = styled(Text)`
    padding-bottom: ${(props) => props.theme.size.static300};
`

const FiltersWrapper = styled.div`
    @media (min-width: ${Breakpoints.b960}px) {
        display: grid;
        grid-template-columns: 2fr 3fr;
        grid-gap: ${(props) => props.theme.size.static400};
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        grid-template-columns: 1fr 3fr;
    }
`

const SelectsWrapper = styled.fieldset`
    border: 0;
    margin-top: ${(props) => props.theme.size.static400};
    padding: 0;

    @media (min-width: ${Breakpoints.b960}px) {
        margin: 0;
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        grid-gap: ${(props) => props.theme.size.static400};
    }
`

export function Filters(): React.JSX.Element {
    const {t} = useTranslation('fa-flotte')
    const dispatch = useDispatch() as AppDispatch

    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)

    // The hosting CMS appends `-app` to every query parameter it forwards
    // to the feature app, while the feature app itself writes them back
    // without the suffix (see `setUrlParams` in utilities.ts). To be
    // resilient in both situations (initial load through the CMS and
    // back-navigation from the dealer form before the CMS has had a chance
    // to rewrite the URL again) we look for the param both with and
    // without the `-app` suffix.
    const readQueryParam = (name: string): string | null =>
        urlParams.get(`${name}-app`) ?? urlParams.get(name)

    const monthlyRangeQuery = readQueryParam(QUERY_PARAMS.monthlyRange)
    const advanceQuery = readQueryParam(QUERY_PARAMS.advance)
    const totKmQuery = readQueryParam(QUERY_PARAMS.totKm)
    const periodQuery = readQueryParam(QUERY_PARAMS.period)
    const modelQuery = readQueryParam(QUERY_PARAMS.model)
    const fuelQuery = readQueryParam(QUERY_PARAMS.fuel)

    const getQueryValues = (
        queryValue: string | null,
        separator: string = Global.COMMA
    ): string[] =>
        queryValue ? queryValue.split(separator).filter(Boolean) : []

    const getMonthlyRangeSteps = (monthlyRangeValues: string[]): number[] => {
        const monthlyRangeSteps: string[] = []

        for (const value of monthlyRangeValues) {
            const s = value.toString()
            if (s === t('filters.slider.values.min').toString()) {
                monthlyRangeSteps.push(t('filters.slider.steps.min'))
            } else if (s === t('filters.slider.values.300').toString()) {
                monthlyRangeSteps.push(t('filters.slider.steps.step1'))
            } else if (s === t('filters.slider.values.400').toString()) {
                monthlyRangeSteps.push(t('filters.slider.steps.step2'))
            } else if (s === t('filters.slider.values.500').toString()) {
                monthlyRangeSteps.push(t('filters.slider.steps.step3'))
            } else if (s === t('filters.slider.values.max').toString()) {
                monthlyRangeSteps.push(t('filters.slider.steps.max'))
            }
        }

        return monthlyRangeSteps.map((value: string) => Number(value))
    }

    const getMonthlyRangeValuesAsNumbers = (
        monthlyRangeValues: string[]
    ): number[] =>
        monthlyRangeValues
            .map((value: string) => Number(value))
            .filter((value: number) => !Number.isNaN(value))

    const monthlyRangeQueryValues = getQueryValues(monthlyRangeQuery, '-')
    const hasQueryFilters = [
        monthlyRangeQuery,
        advanceQuery,
        totKmQuery,
        periodQuery,
        modelQuery,
        fuelQuery,
    ].some((query) => query !== null)
    const areInitialQueryFiltersApplied = useRef(false)

    const [defaultMonthlyRangeValues, setDefaultMonthlyRangeValues] = useState<
        number[]
    >(() => getMonthlyRangeSteps(monthlyRangeQueryValues))
    const [areQueryFiltersApplied, setAreQueryFiltersApplied] = useState(
        !hasQueryFilters
    )

    const [advanceOptions, setAdvanceOptions] = useState<string[]>([])
    const [advanceOptionsLabels, setAdvanceOptionsLabels] = useState<string[]>(
        []
    )
    const [defaultAdvanceValues, setDefaultAdvanceValues] = useState<string[]>(
        () => getQueryValues(advanceQuery)
    )

    const [modelOptions, setModelOptions] = useState<string[]>([])
    const [defaultModelValues, setDefaultModelValues] = useState<string[]>(() =>
        getQueryValues(modelQuery)
    )

    const [fuelOptions, setFuelOptions] = useState<string[]>([])
    const [defaultFuelValues, setDefaultFuelValues] = useState<string[]>(() =>
        getQueryValues(fuelQuery)
    )

    const [periodOptions, setPeriodOptions] = useState<string[]>([])
    const [periodOptionsLabels, setPeriodOptionsLabels] = useState<string[]>([])
    const [defaultPeriodValues, setDefaultPeriodValues] = useState<string[]>(
        () => getQueryValues(periodQuery)
    )

    const [kmOptions, setKmOptions] = useState<string[]>([])
    const [kmOptionsLabels, setKmOptionsLabels] = useState<string[]>([])
    const [defaultTotKmValues, setDefaultTotKmValues] = useState<string[]>(() =>
        getQueryValues(totKmQuery)
    )

    const modelsByPromotions = useSelector(
        (state: RootState) => state.configurationModelsData.modelsByPromotions
    )

    const simulationsList = useSelector(
        (state: RootState) => state.configurationModelsData.simulationsList
    )

    const optionsValues = (options: string[], optionsLabels?: string[]) =>
        options.reduce(
            (acc: Record<string, string>, item: string, index: number) => {
                const label =
                    Array.isArray(optionsLabels) && index < optionsLabels.length
                        ? optionsLabels[index]
                        : item

                return {
                    ...acc,
                    [item]: label,
                }
            },
            {}
        )

    useEffect(() => {
        setModelOptions(modelsByPromotions.map((item: Model) => item.name))
    }, [modelsByPromotions])

    useEffect(() => {
        const advance = simulationsList.map((item: SingleSimulation) =>
            item.advance === '' ||
            item.advance === '.00' ||
            item.advance === '0.00'
                ? Global.NO_ADVANCE
                : Global.ADVANCE
        )
        setAdvanceOptions([...new Set(advance)] as string[])

        const advanceLabels = simulationsList.map((item: SingleSimulation) =>
            item.advance === '' ||
            item.advance === '.00' ||
            item.advance === '0.00'
                ? t('filters.advance.options.withoutAdvance')
                : t('filters.advance.options.withAdvance')
        )

        setAdvanceOptionsLabels([...new Set(advanceLabels)] as string[])

        const fuel = simulationsList.map((item: SingleSimulation) => item.fuel)

        setFuelOptions([...new Set(fuel)] as string[])

        const period = Array.from(
            new Set(
                simulationsList.map(
                    (item: SingleSimulation) => item.num_of_months
                )
            )
        ).sort()

        setPeriodOptions(period as string[])

        const periodLabels = Array.from(
            new Set(
                simulationsList.map(
                    (item: SingleSimulation) =>
                        `${item.num_of_months} ${t('simulation.months')}`
                )
            )
        ).sort()

        setPeriodOptionsLabels(periodLabels as string[])

        const km = Array.from(
            new Set(
                simulationsList.map((item: SingleSimulation) => item.tot_km)
            )
        ).sort()

        setKmOptions(km as string[])

        const kmLabels = Array.from(
            new Set(
                simulationsList.map(
                    (item: SingleSimulation) =>
                        `${numberFormat(item.tot_km)} ${t('simulation.km')}`
                )
            )
        ).sort()
        setKmOptionsLabels(kmLabels as string[])
    }, [simulationsList])

    // The query params may carry filter values written by another instance
    // of this feature app: the CMS mounts it on several pages with
    // different configurations (e.g. serviceType 'fleet' and 'business')
    // and preserves the query string on client-side navigation between
    // them. They can also come from an outdated shared link. Blindly
    // applying them can select values that do not exist in the current
    // dataset, showing "0 results" even though simulations are available.
    // So we wait for the current dataset, keep only the values it actually
    // contains and drop the query filters entirely if their combination
    // would not match any simulation.
    useEffect(() => {
        if (
            areInitialQueryFiltersApplied.current ||
            isEmpty(simulationsList) ||
            isEmpty(modelsByPromotions)
        ) {
            return
        }

        areInitialQueryFiltersApplied.current = true

        if (!hasQueryFilters) {
            setAreQueryFiltersApplied(true)

            return
        }

        const getAdvanceToken = (simulation: SingleSimulation): string =>
            simulation.advance === '' ||
            simulation.advance === '.00' ||
            simulation.advance === '0.00'
                ? Global.NO_ADVANCE
                : Global.ADVANCE

        const keepAvailableValues = (
            queryValues: string[],
            getSimulationValue: (simulation: SingleSimulation) => string
        ): string[] => {
            const availableValues = new Set<string>(
                simulationsList.map(getSimulationValue)
            )

            return queryValues.filter((value) => availableValues.has(value))
        }

        let advanceValues = keepAvailableValues(
            getQueryValues(advanceQuery),
            getAdvanceToken
        )
        let totKmValues = keepAvailableValues(
            getQueryValues(totKmQuery),
            (simulation) => String(simulation.tot_km)
        )
        let periodValues = keepAvailableValues(
            getQueryValues(periodQuery),
            (simulation) => String(simulation.num_of_months)
        )
        let modelValues = keepAvailableValues(
            getQueryValues(modelQuery),
            (simulation) => simulation.model
        )
        let fuelValues = keepAvailableValues(
            getQueryValues(fuelQuery),
            (simulation) => simulation.fuel
        )
        let monthlyRangeValues = getMonthlyRangeValuesAsNumbers(
            monthlyRangeQueryValues
        )

        if (monthlyRangeValues.length !== 2) {
            monthlyRangeValues = []
        }

        // Even when every single value exists in the dataset, their
        // combination may not (e.g. the requested model is never offered
        // with the requested mileage), which would again produce an empty
        // result list.
        const matchesAnySimulation = simulationsList.some(
            (simulation: SingleSimulation) =>
                (isEmpty(modelValues) ||
                    modelValues.includes(simulation.model)) &&
                (isEmpty(fuelValues) || fuelValues.includes(simulation.fuel)) &&
                (isEmpty(periodValues) ||
                    periodValues.includes(String(simulation.num_of_months))) &&
                (isEmpty(totKmValues) ||
                    totKmValues.includes(String(simulation.tot_km))) &&
                (isEmpty(advanceValues) ||
                    advanceValues.includes(getAdvanceToken(simulation))) &&
                (isEmpty(monthlyRangeValues) ||
                    isBetweenRange(
                        simulation.monthly_payment,
                        monthlyRangeValues[0],
                        monthlyRangeValues[1]
                    ))
        )

        if (!matchesAnySimulation) {
            advanceValues = []
            totKmValues = []
            periodValues = []
            modelValues = []
            fuelValues = []
            monthlyRangeValues = []
        }

        if (advanceQuery !== null) {
            setDefaultAdvanceValues(advanceValues)
            dispatch(setAdvance(advanceValues))
        }
        if (totKmQuery !== null) {
            setDefaultTotKmValues(totKmValues)
            dispatch(setTotKm(totKmValues))
        }
        if (periodQuery !== null) {
            setDefaultPeriodValues(periodValues)
            dispatch(setPeriod(periodValues))
        }
        if (modelQuery !== null) {
            setDefaultModelValues(modelValues)
            dispatch(setModels(modelValues))
        }
        if (fuelQuery !== null) {
            setDefaultFuelValues(fuelValues)
            dispatch(setFuels(fuelValues))
        }
        if (monthlyRangeQuery !== null) {
            setDefaultMonthlyRangeValues(
                isEmpty(monthlyRangeValues)
                    ? []
                    : getMonthlyRangeSteps(monthlyRangeQueryValues)
            )
            dispatch(setMonthlyRange(monthlyRangeValues))
        }

        setAreQueryFiltersApplied(true)
    }, [simulationsList, modelsByPromotions])

    return (
        <>
            {!isEmpty(simulationsList) && areQueryFiltersApplied ? (
                <FilterContainer id="filtersBar">
                    <HeaderContainer aria-hidden="true">
                        <TextContainer>
                            <HeaderText
                                tag={TextTag.p}
                                appearance={TokenTextAppearance.headline100}
                                color={TokenTextColor.tertiary}
                            >
                                {t('filters.labels.lease')}
                            </HeaderText>
                        </TextContainer>
                        <TextContainer>
                            <HeaderText
                                tag={TextTag.p}
                                appearance={TokenTextAppearance.headline100}
                                color={TokenTextColor.tertiary}
                            >
                                {t('filters.labels.model')}
                            </HeaderText>
                        </TextContainer>
                    </HeaderContainer>
                    <FiltersWrapper>
                        <FilterSlider
                            title={t('filters.slider.title')}
                            sliderKey={FILTERS_KEYS.monthlyRange}
                            min={0}
                            max={4}
                            step={1}
                            minRangeBetweenThumbs={1}
                            thumbs={[
                                {
                                    defaultValue: defaultMonthlyRangeValues[0]
                                        ? defaultMonthlyRangeValues[0]
                                        : 0,
                                    ariaLabel: t(
                                        'filters.slider.ariaLabels.min'
                                    ),
                                },
                                {
                                    defaultValue: defaultMonthlyRangeValues[1]
                                        ? defaultMonthlyRangeValues[1]
                                        : 4,
                                    ariaLabel: t(
                                        'filters.slider.ariaLabels.max'
                                    ),
                                },
                            ]}
                            highlights={[
                                {
                                    value: t('filters.slider.steps.min'),
                                    label: t('filters.slider.labels.min'),
                                },
                                {
                                    value: t('filters.slider.steps.step1'),
                                    label: t('filters.slider.labels.step1'),
                                },
                                {
                                    value: t('filters.slider.steps.step2'),
                                    label: t('filters.slider.labels.step2'),
                                },
                                {
                                    value: t('filters.slider.steps.step3'),
                                    label: t('filters.slider.labels.step3'),
                                },
                                {
                                    value: t('filters.slider.steps.max'),
                                    label: t('filters.slider.labels.max'),
                                },
                            ]}
                            setElement={setMonthlyRange}
                        ></FilterSlider>

                        <SelectsWrapper>
                            <FilterGroupLegend>
                                {t('filters.labels.model')}
                            </FilterGroupLegend>
                            {!isEmpty(advanceOptions) && (
                                <FilterSelect
                                    options={optionsValues(
                                        advanceOptions,
                                        advanceOptionsLabels
                                    )}
                                    title={t('filters.advance.title')}
                                    defaultValue={defaultAdvanceValues}
                                    setElement={setAdvance}
                                    selectKey={FILTERS_KEYS.advance}
                                ></FilterSelect>
                            )}

                            {!isEmpty(kmOptions) && (
                                <FilterSelect
                                    title={t('filters.km.title')}
                                    options={optionsValues(
                                        kmOptions,
                                        kmOptionsLabels
                                    )}
                                    defaultValue={defaultTotKmValues}
                                    setElement={setTotKm}
                                    selectKey={FILTERS_KEYS.totKm}
                                ></FilterSelect>
                            )}

                            {!isEmpty(periodOptions) && (
                                <FilterSelect
                                    title={t('filters.period.title')}
                                    options={optionsValues(
                                        periodOptions,
                                        periodOptionsLabels
                                    )}
                                    defaultValue={defaultPeriodValues}
                                    setElement={setPeriod}
                                    selectKey={FILTERS_KEYS.period}
                                ></FilterSelect>
                            )}

                            {!isEmpty(modelOptions) && (
                                <FilterSelect
                                    title={t('filters.model.title')}
                                    options={optionsValues(modelOptions)}
                                    setElement={setModels}
                                    defaultValue={defaultModelValues}
                                    selectKey={FILTERS_KEYS.model}
                                ></FilterSelect>
                            )}

                            {!isEmpty(fuelOptions) && (
                                <FilterSelect
                                    title={t('filters.fuel.title')}
                                    options={optionsValues(fuelOptions)}
                                    setElement={setFuels}
                                    defaultValue={defaultFuelValues}
                                    selectKey={FILTERS_KEYS.fuel}
                                ></FilterSelect>
                            )}
                        </SelectsWrapper>
                    </FiltersWrapper>
                </FilterContainer>
            ) : undefined}
        </>
    )
}
